import { SUBSCRIPTION_ACCESS_HEADER } from '@vendepay/contracts';
import { recordCheckoutArrival } from './traffic';
import type { SupportedLocale } from '@vendepay/contracts';
import type { Money, PublicCheckout, PublicCheckoutFunnelNode } from './contract';
import type { CheckoutTrackingIdentifiers, CheckoutUtmAttribution } from './attribution';
import type { CheckoutSkinAssignmentSource } from './skin-bucket';
import type { UpsellWidgetStyle } from './upsell-widget-style';
import { sampleCheckout, sampleRoundingCheckout, sampleSubscriptionCheckout } from './sample';

const API_BASE = import.meta.env.VITE_CHECKOUT_API_URL ?? '/api';
const DEMO_CHECKOUT_IDS = new Set(['demo', 'assinatura', 'sem-cupom', 'arredondamento', '404']);

export function useMock(checkoutId?: string): boolean {
  const flag = import.meta.env.VITE_USE_MOCK;
  if (flag === 'true') return Boolean(import.meta.env.DEV);
  if (flag === 'false') return false;
  return Boolean(import.meta.env.DEV && checkoutId && DEMO_CHECKOUT_IDS.has(checkoutId));
}

export class CheckoutNotFoundError extends Error {}

export class CheckoutUnavailableError extends Error {}

async function throwCheckoutResponseError(response: Response): Promise<never> {
  if (response.status === 409) {
    const body: unknown = await response.json().catch(() => undefined);
    if (typeof body === 'object' && body !== null &&
      'error' in body && body.error === 'PublicCheckoutUnavailableError') {
      throw new CheckoutUnavailableError();
    }
  }
  throw new Error(`BFF respondeu ${response.status}`);
}

export class CheckoutOrderError extends Error {
  constructor(
    message: string,
    readonly bloqueioCodigo?: string | undefined,
  ) {
    super(message);
    this.name = 'CheckoutOrderError';
  }
}

export class CheckoutSessionGoneError extends Error {}

function withMockExperiment(
  checkout: PublicCheckout,
  checkoutId: string,
  checkoutSessionId?: string,
  experimentOverride?: string,
): PublicCheckout {
  const variant = experimentOverride === 'legacy' || experimentOverride === 'new' ? experimentOverride : undefined;
  return {
    ...checkout,
    checkoutId,
    ...(checkoutSessionId ? { checkoutSessionId } : {}),
    ...(variant ? { experiment: { id: 'checkout-skin' as const, variant } } : {}),
  };
}

export interface CheckoutRequestContext {
  country?: string | undefined;
  affiliateRef?: string | undefined;
  checkoutSessionId?: string | undefined;
  offerId?: string | undefined;
  experimentOverride?: string | undefined;
}

export function prepareCheckout(checkoutId: string, context: CheckoutRequestContext & { checkoutSessionId: string }): Promise<PublicCheckout> {
  return requestCheckout(checkoutId, context, 'POST', true);
}

export function fetchCheckout(checkoutId: string, context: CheckoutRequestContext): Promise<PublicCheckout> {
  return requestCheckout(checkoutId, context, 'GET', false);
}

export interface CheckoutVisitResult {
  checkoutSessionId: string;
  experiment: {
    id: 'checkout-skin';
    variant: 'new' | 'legacy';
  };
}

export async function postCheckoutVisit(
  checkoutId: string,
  context: CheckoutRequestContext & { checkoutSessionId: string; variant: 'new' | 'legacy'; variantSource: CheckoutSkinAssignmentSource },
): Promise<CheckoutVisitResult> {
  if (useMock(checkoutId)) {
    return {
      checkoutSessionId: context.checkoutSessionId,
      experiment: { id: 'checkout-skin', variant: context.variant },
    };
  }

  const url = urlWithQuery(`${API_BASE}/checkouts/${encodeURIComponent(checkoutId)}/sessions`, {
    scope: 'visit',
    country: context.country,
    ref: context.affiliateRef,
    offerId: context.offerId,
    var: context.experimentOverride,
  });
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify({ sessionId: context.checkoutSessionId, variant: context.variant, variantSource: context.variantSource }),
    headers: requestHeaders({ accept: 'application/json', 'content-type': 'application/json' }),
  });

  if (!response.ok) {
    return throwCheckoutResponseError(response);
  }

  const result = (await response.json()) as CheckoutVisitResult;
  void recordCheckoutArrival(API_BASE, checkoutId, context.offerId);
  return result;
}

export async function touchCheckoutSession(checkoutId: string, checkoutSessionId: string): Promise<void> {
  if (useMock(checkoutId)) return;

  const response = await fetch(
    `${API_BASE}/checkouts/${encodeURIComponent(checkoutId)}/sessions/${encodeURIComponent(checkoutSessionId)}/touch`,
    { method: 'POST', headers: requestHeaders({ accept: 'application/json' }) },
  );
  if (response.status === 410) {
    throw new CheckoutSessionGoneError('Checkout session is gone');
  }
  if (!response.ok) {
    throw new Error(`BFF respondeu ${response.status}`);
  }
}

export function fetchPublicCheckout(
  checkoutId: string,
  country?: string,
  affiliateRef?: string,
  checkoutSessionId?: string,
  offerId?: string,
  sessionActivity?: 'background',
  experimentOverride?: string,
): Promise<PublicCheckout> {
  return requestCheckout(checkoutId, { country, affiliateRef, checkoutSessionId, offerId, experimentOverride },
    'GET', sessionActivity !== 'background');
}

async function requestCheckout(
  checkoutId: string,
  context: CheckoutRequestContext,
  method: 'GET' | 'POST',
  recordArrival: boolean,
): Promise<PublicCheckout> {
  const { country, affiliateRef, checkoutSessionId, offerId, experimentOverride } = context;
  if (useMock(checkoutId)) {
    if (checkoutId === '404') {
      throw new CheckoutNotFoundError(checkoutId);
    }
    if (checkoutId === 'assinatura') {
      return withMockExperiment(sampleSubscriptionCheckout, checkoutId, checkoutSessionId, experimentOverride);
    }
    if (checkoutId === 'sem-cupom') {
      return { ...withMockExperiment(sampleCheckout, checkoutId, checkoutSessionId, experimentOverride), acceptsCoupon: false };
    }
    if (checkoutId === 'arredondamento') {
      return withMockExperiment(sampleRoundingCheckout, sampleRoundingCheckout.checkoutId, checkoutSessionId, experimentOverride);
    }
    return withMockExperiment(sampleCheckout, checkoutId, checkoutSessionId, experimentOverride);
  }

  const url = urlWithQuery(`${API_BASE}/checkouts/${encodeURIComponent(checkoutId)}${method === 'POST' ? '/sessions' : ''}`, {
    country,
    ref: affiliateRef,
    sessionId: method === 'GET' ? checkoutSessionId : undefined,
    offerId,
    sessionActivity: recordArrival ? undefined : 'background',
    var: experimentOverride,
  });
  const init: RequestInit = method === 'POST'
    ? { method: 'POST', body: JSON.stringify({ sessionId: checkoutSessionId }),
        headers: requestHeaders({ accept: 'application/json', 'content-type': 'application/json' }) }
    : { headers: requestHeaders({ accept: 'application/json' }) };
  const response = await fetch(url, init);

  if (response.status === 404) {
    throw new CheckoutNotFoundError(checkoutId);
  }
  if (!response.ok) {
    return throwCheckoutResponseError(response);
  }

  const checkout = (await response.json()) as PublicCheckout;
  const shouldRecordArrival = recordArrival && checkout.preparation !== 'quote';
  if (shouldRecordArrival) void recordCheckoutArrival(API_BASE, checkoutId, offerId);
  return checkout.checkoutSessionId !== undefined || !shouldRecordArrival
    ? checkout
    : checkoutSessionId
      ? { ...checkout, checkoutSessionId }
      : checkout;
}

export interface CheckoutSessionContactInput {
  readonly email: string;
  readonly name?: string;
  readonly phone?: string;
}

export async function captureCheckoutContact(
  checkoutId: string,
  checkoutSessionId: string,
  input: CheckoutSessionContactInput,
): Promise<{ readonly captured: boolean }> {
  if (useMock(checkoutId)) {
    return { captured: true };
  }

  const response = await fetch(
    `${API_BASE}/checkouts/${encodeURIComponent(checkoutId)}/sessions/${encodeURIComponent(checkoutSessionId)}/contact`,
    {
      method: 'PATCH',
      headers: requestHeaders({ 'content-type': 'application/json', accept: 'application/json' }),
      body: JSON.stringify(input),
      keepalive: true,
    },
  );
  if (!response.ok) {
    throw new Error(`captura de contato respondeu ${response.status}`);
  }
  const result = (await response.json()) as { readonly captured?: unknown };
  return { captured: result.captured === true };
}

export type CouponPreview =
  | { valid: true; code: string; type: 'percent'; percent: number; amountAfterDiscount?: Money }
  | { valid: true; code: string; type: 'fixed'; amount: Money; amountAfterDiscount?: Money }
  | { valid: false; reason?: 'NAO_APLICAVEL' };

export interface CheckoutOrderInput {
  buyerLocale?: SupportedLocale;
  saleId: string;
  checkoutSessionId?: string;
  offerId: string;
  planId?: string;
  paymentMethod: CheckoutCorePaymentMethod;
  paymentMethodToken?: string;
  stripeConfirmationTokenId?: string;
  buyerEmail: string;
  payerName?: string;
  phone?: string;
  payerDocument?: {
    type: 'CPF' | 'CNPJ';
    number: string;
  };
  affiliateRef?: string;
  utm?: CheckoutUtmAttribution;
  trackingIdentifiers?: CheckoutTrackingIdentifiers;
  recuperaaiCartId?: string;
  piiConsent?: boolean;
  selectedBumpIds?: string[];
  couponCode?: string;
  installments?: number;
  currency?: string;
  pricingToken?: string;
  displayedAmount?: Money;
}

export type CheckoutOrderStatus = 'PAID' | 'PENDING' | 'REQUIRES_ACTION' | 'FAILED' | 'AMBIGUOUS';

export interface CheckoutVoucher {
  pixCopyPaste?: string;
  pixQrCodeBase64?: string;
  expiresAt?: string;
}

export interface CheckoutOrderResult {
  aberto?: boolean;
  saleId?: string;
  status?: CheckoutOrderStatus;
  isFreeTrial?: boolean;
  funnelHandoffId?: string;
  amountCharged?: Money;
  paymentMethod?: CheckoutCorePaymentMethod;
  cardLast4?: string;
  cardBrand?: string;
  paidAt?: string;
  bloqueioCodigo?: string;
  voucher?: CheckoutVoucher;
  clientSecret?: string;
  publishableKey?: string;
  declineCode?: string;
  purchaseTracking?: PurchaseTracking;
}

export interface PurchasePixelValue {
  readonly provider: 'facebook' | 'tiktok' | 'google_ads';
  readonly pixelId: string;
  readonly conversionLabel: string;
  readonly value: string;
  readonly currency: string;
}

export type PurchaseTracking =
  | { readonly ready: false; readonly pixels: readonly [] }
  | { readonly ready: true; readonly pixels: readonly PurchasePixelValue[] };

export async function createCheckoutOrder(
  checkoutId: string,
  input: CheckoutOrderInput,
): Promise<CheckoutOrderResult> {
  if (useMock(checkoutId)) {
    return { aberto: true, saleId: input.saleId, status: 'PAID', funnelHandoffId: `mock-handoff:${input.saleId}` };
  }

  const response = await fetch(`${API_BASE}/checkouts/${encodeURIComponent(checkoutId)}/orders`, {
    method: 'POST',
    headers: requestHeaders({ 'content-type': 'application/json', accept: 'application/json' }),
    body: JSON.stringify(input),
  });

  const data = (await response.json().catch(() => ({}))) as CheckoutOrderResult & {
    message?: string;
  };
  if (!response.ok) {
    throw new CheckoutOrderError(
      typeof data.message === 'string' && data.message ? data.message : 'Pagamento recusado.',
      data.bloqueioCodigo,
    );
  }
  return data;
}

export interface FunnelStepInput {
  originalSaleId: string;
  funnelNodeId: string;
  decision: 'ACCEPT' | 'REJECT';
  funnelHandoffId?: string;
}

export type FunnelStepResult =
  | { accepted: false; status: 'SKIPPED'; originalSaleId: string; funnelNodeId: string }
  | (CheckoutOrderResult & { accepted: true });

export interface CheckoutOrderCoupon {
  code: string;
  type: 'percent' | 'fixed';
  percent?: number;
  configuredAmount?: Money;
  amountBeforeDiscount: Money;
  discountAmount: Money;
}

export interface CheckoutOrderSummaryCharge {
  saleId: string;
  couponCode?: string;
  coupon?: CheckoutOrderCoupon;
  kind: 'principal' | 'upsell' | 'downsell';
  title?: string;
  funnelNodeId?: string;
  status: CheckoutOrderStatus | 'REFUND_REQUESTED' | 'PARTIALLY_REFUNDED' | 'REFUNDED' | 'CHARGED_BACK';
  amountCharged: Money;
  paymentMethod?: CheckoutCorePaymentMethod;
  cardLast4?: string;
  cardBrand?: string;
  paidAt?: string;
  items?: { id: string; title: string; amount: Money }[];
  recurring?: { id: string; title: string; amount: Money; billingPeriodUnit: 'MINUTE' | 'DAY' | 'WEEK' | 'MONTH' | 'YEAR'; billingPeriodCount: number }[];
}

export type CheckoutCorePaymentMethod = 'PIX' | 'CREDIT_CARD' | 'BOLETO' | 'APPLE_PAY' | 'GOOGLE_PAY';

export interface CheckoutOrderSummary {
  buyerEmail?: string;
  buyerLocale?: SupportedLocale;
  originalSaleId: string;
  checkoutId: string;
  charges: CheckoutOrderSummaryCharge[];
  totalCaptured?: Money;
}

export type SubscriptionActionKind = 'pay-now' | 'cancel' | 'update-card';
export type SubscriptionActionStatus = 'TRIALING' | 'ACTIVE' | 'PAST_DUE' | 'CANCELED';
export type SubscriptionActionInvoiceStatus =
  | 'PENDING'
  | 'PROCESSING'
  | 'AWAITING_ACTION'
  | 'PAID'
  | 'PAST_DUE'
  | 'IN_GRACE'
  | 'FAILED'
  | 'CANCELED';

export interface SubscriptionActionView {
  buyerLocale?: SupportedLocale;
  action: 'PAY_INVOICE' | 'UPDATE_PAYMENT_METHOD' | 'CANCEL' | 'MANAGE';
  subscription: {
    id: string;
    status: SubscriptionActionStatus;
    currentPeriodEndAt: string;
    cancelAtPeriodEnd: boolean;
    planName?: string;
    chargeAmount?: Money;
  };
  buyerEmailMasked?: string;
  invoice?: {
    id: string;
    status: SubscriptionActionInvoiceStatus;
    dueAt: string;
    value: Money;
    attempts: number;
    nextAttemptAt?: string;
    gracePeriodEndsAt?: string;
  };
  payment?: {
    status: 'PENDING' | 'SUCCEEDED' | 'FAILED' | 'AMBIGUOUS' | 'REQUIRES_ACTION';
    requiresAction: boolean;
    clientSecret?: string;
    publishableKey?: string;
  };
  updateId?: string;
}

export interface FunnelOffer {
  originalSaleId: string;
  checkoutId: string;
  node: PublicCheckoutFunnelNode;
  widgetStyle?: UpsellWidgetStyle | null;
  navigation: {
    accept: FunnelNavigationTarget;
    reject: FunnelNavigationTarget;
    pendingSkip: FunnelNavigationTarget;
  };
}

export type FunnelNavigationTarget =
  | { type: 'hosted'; nodeId: string }
  | { type: 'external'; nodeId: string; url: string }
  | { type: 'complete'; url: string };

export async function fetchFunnelOffer(nodeId: string, funnelHandoffId: string): Promise<FunnelOffer | null> {
  const response = await fetch(urlWithQuery(`${API_BASE}/upsell/offer`, { nodeId, funnelHandoffId }), {
    headers: requestHeaders({ accept: 'application/json' }),
  });

  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`oferta respondeu ${response.status}`);
  }
  return (await response.json()) as FunnelOffer;
}

export async function submitFunnelStep(checkoutId: string, input: FunnelStepInput): Promise<FunnelStepResult> {
  if (useMock(checkoutId)) {
    if (input.decision === 'REJECT') {
      return { accepted: false, status: 'SKIPPED', originalSaleId: input.originalSaleId, funnelNodeId: input.funnelNodeId };
    }
    return {
      accepted: true,
      aberto: true,
      saleId: `funnel:${input.originalSaleId}:${input.funnelNodeId}`,
      status: 'PAID',
    };
  }

  const response = await fetch(`${API_BASE}/checkouts/${encodeURIComponent(checkoutId)}/funnel-step`, {
    method: 'POST',
    headers: requestHeaders({
      'content-type': 'application/json',
      accept: 'application/json',
      'x-vendepay-funnel-request': '1',
    }),
    body: JSON.stringify(input),
  });

  const data = (await response.json().catch(() => ({}))) as FunnelStepResult & { message?: string };
  if (!response.ok) {
    throw new Error(
      typeof data.message === 'string' && data.message ? data.message : 'Não foi possível processar a oferta.',
    );
  }
  return data;
}

export async function fetchOrderStatus(
  checkoutId: string,
  saleId: string,
  funnelHandoffId?: string,
): Promise<CheckoutOrderStatus> {
  return (await fetchOrderResult(checkoutId, saleId, funnelHandoffId)).status ?? 'PENDING';
}

export async function fetchOrderResult(
  checkoutId: string,
  saleId: string,
  funnelHandoffId?: string,
): Promise<CheckoutOrderResult> {
  if (useMock(checkoutId)) {
    return { aberto: true, saleId, status: 'PAID', funnelHandoffId: `mock-handoff:${saleId}` };
  }

  const response = await fetch(
    urlWithQuery(
      `${API_BASE}/checkouts/${encodeURIComponent(checkoutId)}/orders/${encodeURIComponent(saleId)}/status`,
      { funnelHandoffId },
    ),
    { headers: requestHeaders({ accept: 'application/json' }) },
  );

  if (!response.ok) {
    throw new Error(`status ${response.status}`);
  }

  const data = (await response.json()) as CheckoutOrderResult;
  return data;
}

export type PurchaseTrackingFailureReason =
  | 'http_error'
  | 'request_timeout'
  | 'network_error'
  | 'invalid_payload';

export class PurchaseTrackingRequestError extends Error {
  constructor(
    readonly retryable: boolean,
    readonly reason: PurchaseTrackingFailureReason,
  ) {
    super('tracking_unavailable');
    this.name = 'PurchaseTrackingRequestError';
  }
}

export async function fetchPurchaseTracking(
  checkoutId: string,
  saleId: string,
  funnelHandoffId: string,
  signal: AbortSignal,
): Promise<PurchaseTracking | null> {
  if (useMock(checkoutId)) return null;
  const deadline = AbortSignal.timeout(1500);
  const requestSignal = AbortSignal.any([signal, deadline]);
  try {
    const response = await fetch(
      urlWithQuery(
        `${API_BASE}/checkouts/${encodeURIComponent(checkoutId)}/orders/${encodeURIComponent(saleId)}/status`,
        { funnelHandoffId, includePurchaseTracking: 'true' },
      ),
      { headers: requestHeaders({ accept: 'application/json' }), signal: requestSignal },
    );
    if (response.status === 404) return null;
    if (!response.ok) {
      throw new PurchaseTrackingRequestError(
        response.status === 429 || response.status >= 500,
        'http_error',
      );
    }
    const body: unknown = await response.json().catch((error) => {
      if (requestSignal.aborted) throw error;
      return null;
    });
    const parsed = purchaseTrackingFromOrderResult(body);
    if (parsed === undefined) {
      throw new PurchaseTrackingRequestError(false, 'invalid_payload');
    }
    return parsed;
  } catch (error) {
    if (error instanceof PurchaseTrackingRequestError) throw error;
    throw new PurchaseTrackingRequestError(
      true,
      deadline.aborted ? 'request_timeout' : 'network_error',
    );
  }
}

export async function fetchOrderSummary(
  checkoutId: string,
  saleId: string,
  funnelHandoffId?: string,
): Promise<CheckoutOrderSummary | null> {
  if (useMock(checkoutId) || funnelHandoffId === undefined || funnelHandoffId.length === 0) {
    return null;
  }

  const response = await fetch(
    urlWithQuery(
      `${API_BASE}/checkouts/${encodeURIComponent(checkoutId)}/orders/${encodeURIComponent(saleId)}/summary`,
      { funnelHandoffId },
    ),
    { headers: requestHeaders({ accept: 'application/json' }) },
  );

  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`summary ${response.status}`);
  }

  return (await response.json()) as CheckoutOrderSummary;
}

export class SubscriptionActionHttpError extends Error {
  constructor(readonly status: number, readonly code?: string) {
    super('Subscription action request failed');
  }
}

export function isSubscriptionAccessError(error: unknown): boolean {
  return error instanceof SubscriptionActionHttpError && error.status === 400 && error.code === 'InvalidSubscriptionActionTokenError';
}

async function subscriptionResponse<T = SubscriptionActionView>(response: Response): Promise<T> {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new SubscriptionActionHttpError(response.status, typeof data.error === 'string' ? data.error : undefined);
  return data as T;
}

export async function openSubscriptionSession(accessId: string, code: string): Promise<void> {
  await subscriptionResponse(await fetch(`${API_BASE}/subscription-actions/session`, {
    method: 'POST', credentials: 'include', cache: 'no-store',
    headers: requestHeaders({ 'content-type': 'application/json', accept: 'application/json', [SUBSCRIPTION_ACCESS_HEADER]: '1' }),
    body: JSON.stringify({ accessId, code }),
  }));
}

export async function requestSubscriptionLink(accessId: string, action: SubscriptionActionKind, requestId: string): Promise<void> {
  await subscriptionResponse(await fetch(`${API_BASE}/subscription-actions/request-link`, {
    method: 'POST', credentials: 'include', cache: 'no-store',
    headers: requestHeaders({ 'content-type': 'application/json', accept: 'application/json', [SUBSCRIPTION_ACCESS_HEADER]: '1' }),
    body: JSON.stringify({ accessId, action: coreAction(action), requestId }),
  }));
}

export async function fetchSubscriptionAction(action: SubscriptionActionKind, accessId: string): Promise<SubscriptionActionView> {
  const response = await fetch(
    urlWithQuery(`${API_BASE}/subscription-actions/resolve`, {
      action: coreAction(action),
      accessId,
    }),
    { credentials: 'include', cache: 'no-store', headers: requestHeaders({ accept: 'application/json' }) },
  );
  return subscriptionResponse(response);

}

export async function paySubscriptionInvoice(accessId: string): Promise<SubscriptionActionView> {
  return postSubscriptionAction('pay-now', { accessId });
}

export async function confirmSubscriptionInvoice(accessId: string): Promise<SubscriptionActionView> {
  return postSubscriptionAction('pay-now/confirm', { accessId });
}

export async function cancelSubscriptionByToken(accessId: string, atPeriodEnd = true): Promise<SubscriptionActionView> {
  return postSubscriptionAction('cancel', { accessId, atPeriodEnd });
}

export async function updateSubscriptionCard(
  accessId: string,
  updateId: string,
  paymentMethodToken: string,
): Promise<SubscriptionActionView> {
  return postSubscriptionAction('update-card', { accessId, updateId, paymentMethodToken });
}

export async function fetchSubscriptionCardUpdateStatus(accessId: string, updateId: string): Promise<SubscriptionActionView> {
  const response = await fetch(
    urlWithQuery(`${API_BASE}/subscription-actions/update-card/status`, {
      accessId,
      updateId,
    }),
    { credentials: 'include', cache: 'no-store', headers: requestHeaders({ accept: 'application/json' }) },
  );
  return subscriptionResponse(response);
}

async function postSubscriptionAction(
  path: 'pay-now' | 'pay-now/confirm' | 'cancel' | 'update-card',
  payload: unknown,
): Promise<SubscriptionActionView> {
  const response = await fetch(`${API_BASE}/subscription-actions/${path}`, {
    method: 'POST', credentials: 'include', cache: 'no-store',
    headers: requestHeaders({ 'content-type': 'application/json', accept: 'application/json', [SUBSCRIPTION_ACCESS_HEADER]: '1' }),
    body: JSON.stringify(payload),
  });
  return subscriptionResponse(response);
}

function coreAction(action: SubscriptionActionKind): SubscriptionActionView['action'] {
  if (action === 'pay-now') {
    return 'PAY_INVOICE';
  }
  if (action === 'update-card') {
    return 'UPDATE_PAYMENT_METHOD';
  }
  return 'CANCEL';
}

export async function previewCoupon(checkoutId: string, code: string, pricingToken?: string, offerId?: string): Promise<CouponPreview> {
  if (useMock(checkoutId)) {
    return code.trim().toUpperCase() === 'OFF10'
      ? { valid: true, code: 'OFF10', type: 'percent', percent: 10 }
      : { valid: false };
  }

  const response = await fetch(
    urlWithQuery(`${API_BASE}/checkouts/${encodeURIComponent(checkoutId)}/coupons/${encodeURIComponent(code)}`, {
      pricingToken,
      offerId,
    }),
    { headers: requestHeaders({ accept: 'application/json' }) },
  );

  if (!response.ok) {
    return { valid: false };
  }

  return (await response.json()) as CouponPreview;
}

function urlWithQuery(base: string, query: Record<string, string | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value) {
      params.set(key, value);
    }
  }
  const serialized = params.toString();
  return serialized ? `${base}?${serialized}` : base;
}

function purchaseTrackingFromOrderResult(value: unknown): PurchaseTracking | undefined {
  if (!isRecord(value)) return undefined;
  const tracking = value.purchaseTracking;
  if (!isRecord(tracking) || typeof tracking.ready !== 'boolean' || !Array.isArray(tracking.pixels)) {
    return undefined;
  }
  if (!tracking.ready) {
    return tracking.pixels.length === 0 ? { ready: false, pixels: [] } : undefined;
  }
  const pixels: PurchasePixelValue[] = [];
  for (const value of tracking.pixels) {
    const pixel = purchasePixelValue(value);
    if (pixel === undefined) return undefined;
    pixels.push(pixel);
  }
  return { ready: true, pixels };
}

function purchasePixelValue(value: unknown): PurchasePixelValue | undefined {
  if (!isRecord(value)) return undefined;
  const expectedKeys = ['conversionLabel', 'currency', 'pixelId', 'provider', 'value'];
  if (Object.keys(value).sort().join('|') !== expectedKeys.join('|')) return undefined;
  if (value.provider !== 'facebook' && value.provider !== 'tiktok' && value.provider !== 'google_ads') {
    return undefined;
  }
  if (
    typeof value.pixelId !== 'string' || value.pixelId.trim().length === 0 ||
    typeof value.conversionLabel !== 'string' ||
    typeof value.value !== 'string' || !/^\d+\.\d{2}$/.test(value.value) || !Number.isFinite(Number(value.value)) ||
    typeof value.currency !== 'string' || !/^[A-Z]{3}$/.test(value.currency)
  ) {
    return undefined;
  }
  return {
    provider: value.provider,
    pixelId: value.pixelId,
    conversionLabel: value.conversionLabel,
    value: value.value,
    currency: value.currency,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function requestHeaders(headers: Record<string, string>): Record<string, string> {
  return { ...headers, 'x-request-id': createRequestId() };
}

function createRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
